import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { WatchQueryFetchPolicy } from '@apollo/client';

import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';

import EnterpriseProgramsByOrganizationSlug from '../queries/EnterpriseProgramsByOrganizationSlug.graphql';
import type {
  EnterpriseProgramsByOrganizationSlugQuery,
  EnterpriseProgramsByOrganizationSlugQueryVariables,
} from '../queries/__generated__/EnterpriseProgramsByOrganizationSlug';

export const useEnterpriseProgramsByOrganizationSlug = (
  organizationSlug: string,
  subdomain?: 'LEARNERS',
  includeArchived?: boolean,
  limit?: number,
  // Optional override; defaults to Apollo's cache-first. Callers that need fresh data on mount
  // (e.g. the Groups listing) can opt into 'cache-and-network'.
  fetchPolicy?: WatchQueryFetchPolicy
) => {
  const { data, loading, error, refetch } = useQuery<
    EnterpriseProgramsByOrganizationSlugQuery,
    EnterpriseProgramsByOrganizationSlugQueryVariables
  >(EnterpriseProgramsByOrganizationSlug, {
    variables: { organizationSlug, includeArchived, limit },
    context: { clientName: 'gatewayGql' },
    fetchPolicy,
  });
  const { hasPermission, getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const isOrgAdmin = getIsOrganizationDomainAdmin();
  const programs = useMemo(
    () =>
      data?.EnterpriseProgram?.queryByOrganizationSlug.elements
        .filter(
          (program) =>
            isOrgAdmin ||
            hasPermission({
              domain: 'PROGRAM',
              domainId: program.id,
              verb: 'VIEW',
              subdomain,
            })
        )
        .map((_) => ({ id: _.id, name: _.name, slug: _.slug, endedAt: _.endedAt })),
    [data?.EnterpriseProgram, hasPermission, subdomain, isOrgAdmin]
  );
  return { programs, loading, error, refetch };
};
